import Logo from "../../assets/image/logo.png";
import { useState } from "react";
import {
    Button, TextField, Box, Typography, Alert, InputAdornment, IconButton, Grid2 as Grid
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { requestRecoverPassword, resetPassword } from "../../services/authServices";
import { delay } from "../../utils/formatters";
import AuthLayout from "./AuthLayout";

function RecoverPassword() {
    const navigate = useNavigate();
    const location = useLocation();

    // Determina se siamo in fase di richiesta email o reset password (presenza token)
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    const isResetPhase = !!token;

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [success, setSuccess] = useState(false);
    const [requestError, setRequestError] = useState(false);

    // Requisiti password (corrispondenti alla logica backend)
    const passwordRequirements = [
        { id: "length", label: "Almeno 8 caratteri", met: password.length >= 8 },
        { id: "upper", label: "Una maiuscola", met: /[A-Z]/.test(password) },
        { id: "lower", label: "Una minuscola", met: /[a-z]/.test(password) },
        { id: "number", label: "Un numero", met: /\d/.test(password) },
        { id: "special", label: "Carattere speciale", met: /[@#$%^&+=!*]/.test(password) },
        { id: "match", label: "Le password coincidono", met: password !== "" && password === confirmPassword },
    ];

    const allRequirementsMet = passwordRequirements.every(req => req.met);

    const handleRequest = async (e) => {
        if (e) e.preventDefault();
        setError("");

        if (!email) {
            setError("Inserisci il tuo indirizzo email.");
            return;
        }

        setLoading(true);
        try {
            await requestRecoverPassword(email);
            setLoading(false);
            setShowOverlay(true);
            setRequestError(false);

            await delay(1000);
            setSuccess(true);

            await delay(3000);
            navigate("/login/admin");
        } catch (err) {
            setLoading(false);
            setShowOverlay(true);
            setSuccess(false);
            
            await delay(1000);
            setRequestError(true);
            setError(err.message || "Impossibile inviare la richiesta. Riprova.");
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setError("");

        if (!password || !confirmPassword) {
            setError("Compila entrambi i campi password.");
            return;
        }

        if (password !== confirmPassword) {
            setError("Le password non coincidono.");
            return;
        }

        if (!allRequirementsMet) {
            setError("La password non soddisfa tutti i requisiti di sicurezza.");
            return;
        }

        setLoading(true);
        try {
            await resetPassword(token, password);
            setLoading(false);
            setShowOverlay(true);
            setRequestError(false);

            await delay(1000);
            setSuccess(true);

            await delay(2500);
            navigate("/login/admin");
        } catch (err) {
            setLoading(false);
            setShowOverlay(true);
            setSuccess(false);
            
            await delay(1000);
            setRequestError(true);
            setError(err.message || "Errore durante il reset della password.");
        }
    };

    const handleRetry = () => {
        setShowOverlay(false);
        setSuccess(false);
        setRequestError(false);
        setError("");
        navigate("/recupero-password", { replace: true });
    };

    const getButtonText = () => {
        if (loading) return "Elaborazione...";
        return isResetPhase ? "AGGIORNA PASSWORD" : "INVIA LINK";
    };

    const isTokenError = error?.toLowerCase().includes("token") || error?.toLowerCase().includes("scaduto");

    const renderErrorActions = () => (
        isTokenError ? (
            <Button 
                variant="outlined" 
                color="inherit" 
                onClick={handleRetry}
                sx={{ mt: 2, borderRadius: '20px', borderColor: 'rgba(255,255,255,0.5)' }}
            >
                Richiedi nuovo link
            </Button>
        ) : (
            <Button 
                variant="outlined" 
                color="inherit" 
                onClick={() => setShowOverlay(false)}
                sx={{ mt: 2, borderRadius: '20px', borderColor: 'rgba(255,255,255,0.5)' }}
            >
                Chiudi e riprova
            </Button>
        )
    );

    return (
        <AuthLayout
            title={`Wow - ${isResetPhase ? "Reset Password" : "Recupero Password"}`}
            showOverlay={showOverlay}
            success={success}
            error={requestError}
            loadingMessage={isResetPhase ? "Aggiornamento password in corso..." : "Invio richiesta in corso..."}
            successTitle={isResetPhase ? "Password Aggiornata!" : "Email Inviata!"}
            successMessage={isResetPhase 
                ? "La tua password è stata resettata con successo." 
                : "Controlla la tua casella di posta per il link di reset."}
            errorMessage={error}
            renderCustomErrorActions={renderErrorActions}
        >
            <img src={Logo} alt="Logo" height="80px" style={{ objectFit: "contain" }} />

            <Box sx={{ textAlign: "center", width: "100%" }}>
                <Typography variant="h5" component="h1" sx={{ color: "#0b3d87", fontWeight: "bold", mb: 0.5 }}>
                    {isResetPhase ? "Nuova Password" : "Recupero Password"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                    {isResetPhase 
                        ? "Imposta le tue nuove credenziali di accesso" 
                        : "Inserisci la tua email per ricevere il link di reset"}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ width: "100%" }}>
                    {error}
                </Alert>
            )}

            <Box 
                component="form" 
                onSubmit={isResetPhase ? handleReset : handleRequest} 
                sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2.5 }}
            >
                {isResetPhase ? (
                    <>
                        <TextField
                            fullWidth
                            placeholder="Nuova Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': { backgroundColor: '#e3ebf3', borderRadius: '4px', '& fieldset': { border: 'none' } },
                                '& .MuiInputBase-input': { padding: '12px 14px', color: '#0b3d87', fontWeight: 500 }
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            placeholder="Conferma Password"
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': { backgroundColor: '#e3ebf3', borderRadius: '4px', '& fieldset': { border: 'none' } },
                                '& .MuiInputBase-input': { padding: '12px 14px', color: '#0b3d87', fontWeight: 500 }
                            }}
                        />
                        {/* Password Strength Meter */}
                        <Box sx={{ width: '100%', mt: -1, mb: 1 }}>
                            <Grid container spacing={1}>
                                {passwordRequirements.map((req) => (
                                    <Grid size={6} key={req.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box 
                                                sx={{ 
                                                    width: 8, height: 8, borderRadius: '50%', 
                                                    backgroundColor: req.met ? '#4caf50' : '#e0e0e0',
                                                    transition: 'all 0.3s'
                                                }} 
                                            />
                                            <Typography variant="caption" sx={{ color: req.met ? '#2e7d32' : '#64748b', fontSize: '0.65rem', fontWeight: 600 }}>
                                                {req.label}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </>
                ) : (
                    <TextField
                        fullWidth
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#e3ebf3',
                                borderRadius: '4px',
                                '& fieldset': { border: 'none' },
                            },
                            '& .MuiInputBase-input': {
                                padding: '12px 14px',
                                color: '#0b3d87',
                                fontWeight: 500,
                            }
                        }}
                    />
                )}

                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                        backgroundColor: '#0b3d87',
                        color: '#fff',
                        borderRadius: '24px',
                        py: 1.2,
                        fontWeight: 'bold',
                        width: "100%",
                        mt: 1,
                        '&:hover': { backgroundColor: '#072b66' }
                    }}
                >
                    {getButtonText()}
                </Button>

                <Button
                    onClick={() => navigate("/login/admin")}
                    startIcon={<ArrowBack />}
                    sx={{ color: '#0b3d87', fontWeight: 600, fontSize: '0.8rem', alignSelf: 'center' }}
                >
                    Torna al Login
                </Button>
            </Box>
        </AuthLayout>
    );
}

export default RecoverPassword;
 